/**
 * Schema Builder - Visual UI for building JSON schemas
 * Supports two layout modes:
 *   1. Single Layout - fields in one flat list
 *   2. Tab Layout    - multiple named tabs, each with independent field lists
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Button,
  Chip,
  Divider,
  Collapse,
  Paper,
  Tooltip,
  InputBase,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  DragHandle as DragHandleIcon,
  ViewStream,
} from '@mui/icons-material';
import { CONTROL_REGISTRY, SettingConfig } from '../../../base/controls/ControlRegistry';
import Editor from '@monaco-editor/react';
import CategoryService from '../../../../../services/CategoryService';
import { BaseTextField } from '../../../base/BaseTextField';
import { BaseSelect } from '../../../base/BaseSelect';
import { BaseCheckbox } from '../../../base/BaseCheckbox';

// --- Types ---

export interface TabChild {
  id: string;
  propName: string;    // key in x-control.childs object
  fields: FieldDef[];  // fields inside this tab panel
}

export interface FieldDef {
  id: string;
  propName: string;         // key in schema properties object
  label: string;            // display label shown to user in CMS UI
  description: string;
  required: boolean;
  controlType: string;
  settings: Record<string, any>;
  childs: FieldDef[];       // for block/array/collapse (array)
  tabChilds: TabChild[];    // for tab control (named object)
}

interface OptionItem { label: string; value: string; }

// SettingConfig is imported from ControlRegistry � no local definition needed.
// CONTROL_TYPES_META and SETTINGS_CONFIG are no longer hardcoded here.
// Use CONTROL_REGISTRY directly at render time.

// --- Utilities ---

let _idCounter = 0;
const genId = () => `f${++_idCounter}_${Math.random().toString(36).slice(2, 5)}`;

const newField    = (type = 'string'): FieldDef    => ({ id: genId(), propName: '', label: '', description: '', required: false, controlType: type, settings: {}, childs: [], tabChilds: [] });
const newTabChild = (propName?: string): TabChild  => ({ id: genId(), propName: propName ?? 'tab_1', fields: [newField('string')] });

function entryToField(entry: Record<string, any>, key = ''): FieldDef {
  const ctrl = entry['x-control'] || {};
  // Tab control: childs is an object on x-control directly (not in settings)
  if (ctrl.type === 'tab') {
    const childsObj: Record<string, any> = ctrl.childs || {};
    const tabChilds: TabChild[] = Object.entries(childsObj).map(([tabKey, childEntry]) => {
      const ce = childEntry as Record<string, any>;
      if (ce['x-control']?.type === 'block') {
        const inner: Record<string, any> = ce['x-control']?.settings?.childs || {};
        const innerFields = Array.isArray(inner)
          ? inner.map((e: any) => entryToField(e))
          : Object.entries(inner).map(([k, e]) => entryToField(e as Record<string, any>, k));
        return { id: genId(), propName: tabKey, fields: innerFields };
      }
      return { id: genId(), propName: tabKey, fields: [entryToField(ce, tabKey)] };
    });
    // label: prefer x-control.label, fallback to old entry.title, then key
    const label = ctrl.label || entry.title || key;
    return { id: genId(), propName: key, label, description: entry.description || '', required: !!entry.required, controlType: 'tab', settings: {}, childs: [], tabChilds };
  }
  const raw  = { ...(ctrl.settings || {}) };
  const rawChilds = raw.childs;
  let kids: Array<[string, any]>;
  if (Array.isArray(rawChilds)) {
    kids = rawChilds.map((e: any) => ['', e]);
  } else if (rawChilds && typeof rawChilds === 'object') {
    kids = Object.entries(rawChilds);
  } else {
    kids = [];
  }
  delete raw.childs;
  const label = ctrl.label || entry.title || key;
  return { id: genId(), propName: key, label, description: entry.description || '', required: !!entry.required, controlType: ctrl.type || 'string', settings: raw, childs: kids.map(([k, e]) => entryToField(e, k)), tabChilds: [] };
}

function fieldToEntry(f: FieldDef): Record<string, any> {
  // Tab control: serialize to x-control.childs as a named object (not settings.childs)
  if (f.controlType === 'tab') {
    const childsObj = Object.fromEntries(
      (f.tabChilds || []).map(tc => [
        tc.propName,
        tc.fields.length === 1
          ? fieldToEntry(tc.fields[0])
          : { 'x-control': { type: 'block', settings: { childs: Object.fromEntries(tc.fields.map(c => [c.propName || c.id, fieldToEntry(c)])) } } },
      ])
    );
    const xCtrl: Record<string, any> = { type: 'tab', childs: childsObj };
    if (f.label) xCtrl.label = f.label;
    const entry: Record<string, any> = { 'x-control': xCtrl };
    if (f.description) entry.description = f.description;
    if (f.required)    entry.required    = true;
    return entry;
  }
  const settings: Record<string, any> = { ...f.settings };
  if (f.childs.length > 0) {
    if (f.controlType === 'block') {
      // block childs: named object keyed by child.propName
      settings.childs = Object.fromEntries(
        f.childs.map(child => {
          const key = child.propName || child.label?.toLowerCase().replace(/\s+/g, '_') || child.id;
          return [key, fieldToEntry(child)];
        })
      );
    } else {
      settings.childs = f.childs.map(fieldToEntry);
    }
  }
  const xControl: Record<string, any> = { type: f.controlType, settings };
  if (f.label) xControl.label = f.label;
  if (f.propName) xControl.propName = f.propName;  // used by CollapseControl to derive data key
  const entry: Record<string, any> = { 'x-control': xControl };
  if (f.description) entry.description = f.description;
  if (f.required)    entry.required    = true;
  return entry;
}

const SCHEMA_META_KEYS = new Set(['$schema', 'type', 'title', 'description', 'required', 'default', 'properties', 'additionalProperties']);

function parseSchemaToFields(json: string): FieldDef[] {
  let schema: Record<string, any> = {};
  try { schema = JSON.parse(json || '{}'); } catch { /* ignore */ }
  if (!schema || typeof schema !== 'object' || Object.keys(schema).length === 0) return [];
  if (schema['x-control']) return [entryToField(schema)];
  const source    = schema.properties || schema;
  const fieldKeys = Object.keys(source).filter(k => !SCHEMA_META_KEYS.has(k) && source[k]?.['x-control']);
  return fieldKeys.map(k => entryToField(source[k], k));
}

function buildSchemaFromFields(fields: FieldDef[]): Record<string, any> {
  if (fields.length === 0) return {};
  if (fields.length === 1) return fieldToEntry(fields[0]);
  return { properties: Object.fromEntries(fields.map(f => [f.propName || f.id, fieldToEntry(f)])) };
}

// --- OptionsEditor ---

const OptionsEditor: React.FC<{ options: OptionItem[]; onChange: (o: OptionItem[]) => void }> = ({ options, onChange }) => {
  const add    = () => onChange([...options, { label: '', value: '' }]);
  const remove = (i: number) => onChange(options.filter((_, x) => x !== i));
  const upd    = (i: number, k: 'label' | 'value', v: string) => onChange(options.map((o, x) => x === i ? { ...o, [k]: v } : o));
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Static Options</Typography>
      {options.map((opt, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
          <BaseTextField size="small" placeholder="Label" value={opt.label} onChange={e => upd(i, 'label', e.target.value)} sx={{ flex: 1 }} />
          <BaseTextField size="small" placeholder="Value" value={opt.value} onChange={e => upd(i, 'value', e.target.value)} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => remove(i)} color="error"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
      ))}
      <Button size="small" variant="text" startIcon={<AddIcon />} onClick={add} sx={{ color: '#005e5d', fontSize: '11px' }}>Add Option</Button>
    </Box>
  );
};

// --- JsonSettingField ---

const JsonSettingField: React.FC<{
  cfg: SettingConfig;
  value: any;
  onChange: (v: any) => void;
}> = ({ cfg, value, onChange }) => {
  const [jsonError, setJsonError] = React.useState('');
  const editorText = React.useRef<string>(
    value !== undefined && value !== null ? JSON.stringify(value, null, 2) : ''
  );

  const handleChange = (v: string | undefined) => {
    const text = v ?? '';
    editorText.current = text;
    const trimmed = text.trim();
    if (!trimmed) {
      setJsonError('');
      onChange(undefined);
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      setJsonError('');
      onChange(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>
        {cfg.label}
      </Typography>
      <Box sx={{ border: `1px solid ${jsonError ? '#f44336' : '#e0e0e0'}`, borderRadius: '4px', overflow: 'hidden' }}>
        <Editor
          height={160}
          defaultLanguage="json"
          defaultValue={editorText.current}
          onChange={handleChange}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            formatOnPaste: true,
            formatOnType: true,
            fontSize: 12,
            fontFamily: '"Fira Code", "Cascadia Code", monospace',
            lineNumbers: 'off',
            folding: false,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            renderLineHighlight: 'none',
            scrollbar: { vertical: 'auto', horizontal: 'hidden' },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: jsonError ? '#f44336' : '#999', mt: 0.5, display: 'block' }}>
        {jsonError || 'filterGroup JSON — luôn được merge với filter của user khi fetch lookup'}
      </Typography>
    </Box>
  );
};

// --- FieldSettingsForm ---

const FieldSettingsForm: React.FC<{
  controlType: string; settings: Record<string, any>; onChange: (s: Record<string, any>) => void;
}> = ({ controlType, settings, onChange }) => {
  const configs = (CONTROL_REGISTRY[controlType]?.settingsConfig || []).filter(c => !c.showIf || c.showIf(settings));
  const [categories, setCategories] = React.useState<{ id: string; name: string }[]>([]);

  React.useEffect(() => {
    const hasCatSelect = configs.some(c => c.type === 'category-select');
    if (hasCatSelect) {
      CategoryService.getAllCategories().then((cats: any[]) => {
        setCategories(cats.map(c => ({ id: c._id || c.id || '', name: c.name })));
      }).catch(() => {});
    }
  }, [controlType]);

  if (configs.length === 0) return <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>No additional settings</Typography>;
  const set = (key: string, value: any) => onChange({ ...settings, [key]: value });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {configs.map(cfg => {
        if (cfg.type === 'options')     return <OptionsEditor key={cfg.key} options={Array.isArray(settings[cfg.key]) ? settings[cfg.key] : []} onChange={v => set(cfg.key, v)} />;
        if (cfg.type === 'toggle')      return <BaseCheckbox key={cfg.key} label={cfg.label} checked={!!settings[cfg.key]} onChange={checked => set(cfg.key, checked)} />;
        if (cfg.type === 'json')        return <JsonSettingField key={cfg.key} cfg={cfg} value={settings[cfg.key]} onChange={v => set(cfg.key, v)} />;
        if (cfg.type === 'select-enum') return (
          <BaseSelect
            key={cfg.key}
            label={cfg.label}
            size="small"
            fullWidth
            value={settings[cfg.key] || cfg.options?.[0] || ''}
            onChange={e => set(cfg.key, e.target.value)}
            options={(cfg.options || []).map(o => ({ label: o, value: o }))}
          />
        );
        if (cfg.type === 'category-select') return (
          <BaseSelect
            key={cfg.key}
            label={cfg.label}
            size="small"
            fullWidth
            showEmpty
            emptyLabel="— None —"
            value={settings[cfg.key] || ''}
            onChange={e => set(cfg.key, e.target.value)}
            options={categories.map(cat => ({ label: cat.name, value: cat.name }))}
          />
        );
        return (
          <BaseTextField
            key={cfg.key}
            label={cfg.label}
            size="small"
            fullWidth
            type={cfg.type === 'number' ? 'number' : 'text'}
            value={settings[cfg.key] ?? ''}
            placeholder={cfg.placeholder ?? ''}
            onChange={e => set(cfg.key, cfg.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
          />
        );
      })}
    </Box>
  );
};

// --- TypePickerPopover ---

const TypePickerPopover: React.FC<{ onSelect: (t: string) => void; onClose: () => void }> = ({ onSelect, onClose }) => (
  <Paper elevation={6} sx={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, p: 2, maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: '10px' }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', mb: 1, display: 'block' }}>Select Control Type</Typography>
    {(['core', 'advanced', 'specialized'] as const).map(tier => {
      const types = Object.entries(CONTROL_REGISTRY)
        .filter(([_, entry]) => entry.tier === tier)
        .map(([type, entry]) => ({ type, ...entry }));
      return (
        <Box key={tier} sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#bbb', textTransform: 'uppercase', fontSize: '10px', letterSpacing: 0.5 }}>{tier}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {types.map(t => (
              <Chip key={t.type}
                icon={<Box sx={{ color: t.color || '#999', display: 'flex', alignItems: 'center' }}>{t.icon}</Box>}
                label={t.label} size="small"
                onClick={() => { onSelect(t.type); onClose(); }}
                sx={{ cursor: 'pointer', bgcolor: `${t.color || '#999'}12`, color: '#333', border: `1px solid ${t.color || '#999'}35`, fontSize: '11px', fontWeight: 500, '& .MuiChip-icon': { color: t.color }, '&:hover': { bgcolor: `${t.color || '#999'}22` } }}
              />
            ))}
          </Box>
        </Box>
      );
    })}
    <Divider sx={{ my: 1 }} />
    <Button size="small" onClick={onClose} sx={{ color: '#999', fontSize: '11px' }}>Cancel</Button>
  </Paper>
);

// --- AddFieldButton ---

const AddFieldButton: React.FC<{ onAdd: (type: string) => void; label?: string }> = ({ onAdd, label = 'Add Field' }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ position: 'relative' }}>
      <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setOpen(!open)}
        sx={{ color: '#005e5d', borderColor: '#005e5d', borderStyle: 'dashed', width: '100%', fontSize: '12px', fontWeight: 600, '&:hover': { bgcolor: 'rgba(0,94,93,0.05)', borderStyle: 'solid' } }}>
        {label}
      </Button>
      {open && <TypePickerPopover onSelect={onAdd} onClose={() => setOpen(false)} />}
    </Box>
  );
};

// --- TabControlEditor (used inside FieldCard when controlType === 'tab') ---

const TabControlEditor: React.FC<{
  tabChilds: TabChild[];
  onChange: (tc: TabChild[]) => void;
  depth: number;
}> = ({ tabChilds, onChange, depth }) => {
  const [activeId, setActiveId] = useState<string>(() => tabChilds[0]?.id ?? '');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const active = tabChilds.find(t => t.id === activeId) ?? tabChilds[0];

  const addTab = () => {
    const n = newTabChild(`tab_${tabChilds.length + 1}`);
    const next = [...tabChilds, n];
    onChange(next);
    setActiveId(n.id);
  };

  const deleteTab = (id: string) => {
    const next = tabChilds.filter(t => t.id !== id);
    onChange(next);
    if (activeId === id) setActiveId(next[0]?.id ?? '');
  };

  const startEdit = (id: string, propName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(id);
    setDraft(propName);
    setTimeout(() => inputRef.current?.select(), 30);
  };

  const commitEdit = () => {
    if (editingId && draft.trim())
      onChange(tabChilds.map(t => t.id === editingId ? { ...t, propName: draft.trim().toLowerCase().replace(/\s+/g, '_') } : t));
    setEditingId(null);
  };

  const updateTabFields = (id: string, fields: FieldDef[]) =>
    onChange(tabChilds.map(t => t.id === id ? { ...t, fields } : t));

  const COLOR = '#0288D1';

  return (
    <Box>
      <Divider sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: COLOR, fontWeight: 700, px: 1 }}>Tab Panels ({tabChilds.length})</Typography>
      </Divider>

      {/* Tab strip */}
      <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: `2px solid #e3f2fd`, mb: 1.5, overflowX: 'auto' }}>
        {tabChilds.map(tab => {
          const isActive  = tab.id === (activeId || tabChilds[0]?.id);
          const isEditing = tab.id === editingId;
          return (
            <Box key={tab.id} onClick={() => setActiveId(tab.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.6,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                borderBottom: isActive ? `2px solid ${COLOR}` : '2px solid transparent',
                mb: '-2px', color: isActive ? COLOR : '#666',
                bgcolor: isActive ? `${COLOR}0d` : 'transparent',
                borderRadius: '4px 4px 0 0', transition: 'all 0.15s',
                '&:hover': { color: COLOR, bgcolor: `${COLOR}08` },
              }}
            >
              {isEditing ? (
                <InputBase
                  inputRef={inputRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingId(null); }}
                  onClick={e => e.stopPropagation()}
                  sx={{ fontSize: '12px', fontWeight: 600, width: Math.max(draft.length * 8, 60) }}
                  autoFocus
                />
              ) : (
                <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: isActive ? 700 : 500, fontFamily: 'monospace' }}
                  onDoubleClick={e => startEdit(tab.id, tab.propName, e)}>
                  {tab.propName}
                </Typography>
              )}
              {tabChilds.length > 1 && (
                <IconButton size="small" onClick={e => { e.stopPropagation(); deleteTab(tab.id); }}
                  sx={{ p: 0.1, color: isActive ? `${COLOR}80` : '#ccc', '&:hover': { color: '#f44336' } }}>
                  <DeleteIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </Box>
          );
        })}
        <Tooltip title="Add tab">
          <IconButton size="small" onClick={addTab}
            sx={{ ml: 0.5, color: COLOR, '&:hover': { bgcolor: `${COLOR}12` } }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Active tab tip */}
      <Typography variant="caption" sx={{ color: '#90CAF9', mb: 0.5, display: 'block' }}>
        prop key: <strong style={{ fontFamily: 'monospace', color: COLOR }}>{active?.propName}</strong>
        {' '}� double-click to rename
      </Typography>

      {/* Active tab fields */}
      {active && (
        <FieldList
          key={active.id}
          fields={active.fields}
          onChange={fields => updateTabFields(active.id, fields)}
          emptyLabel={`No fields in "${active.propName}"`}
        />
      )}
    </Box>
  );
};

// --- FieldCard ---

const FieldCard: React.FC<{
  field: FieldDef;
  onChange: (f: FieldDef) => void;
  onDelete: () => void;
  depth?: number;
  hideDelete?: boolean;
}> = ({ field, onChange, onDelete, depth = 0, hideDelete = false }) => {
  const [expanded, setExpanded] = useState(false);
  // Local text state — prevents re-render cascade on every keystroke
  const [localLabel, setLocalLabel] = useState(field.label);
  const [localPropName, setLocalPropName] = useState(field.propName);
  const [localDesc, setLocalDesc] = useState(field.description);
  const fieldRef = useRef(field);
  fieldRef.current = field;
  const pendingRef = useRef<Partial<FieldDef>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local text state when a different field is mounted (identified by id)
  useEffect(() => {
    setLocalLabel(field.label);
    setLocalPropName(field.propName);
    setLocalDesc(field.description);
    pendingRef.current = {};
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
  }, [field.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flushPending = useCallback(() => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    const pending = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(pending).length > 0) onChange({ ...fieldRef.current, ...pending });
  }, [onChange]);

  const debouncedTextChange = (patch: Partial<FieldDef>) => {
    Object.assign(pendingRef.current, patch);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushPending, 300);
  };

  // Immediate update — flushes any pending text changes to avoid stale merges
  const update = (patch: Partial<FieldDef>) => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    const pending = { ...pendingRef.current };
    pendingRef.current = {};
    onChange({ ...fieldRef.current, ...pending, ...patch });
  };

  const meta      = CONTROL_REGISTRY[field.controlType];
  const hasChilds = meta?.hasChilds ?? false;
  const isTab     = field.controlType === 'tab';

  const addChild    = (type: string) => update({ childs: [...fieldRef.current.childs, newField(type)] });
  const updateChild = (i: number, f: FieldDef) => { const c = [...fieldRef.current.childs]; c[i] = f; update({ childs: c }); };
  const removeChild = (i: number) => update({ childs: fieldRef.current.childs.filter((_, x) => x !== i) });

  const bg     = depth === 0 ? '#fff' : depth === 1 ? '#fafafa' : '#f3f4f6';
  const border = expanded ? (meta?.color || '#005e5d') : '#e0e0e0';

  const isCollapseCtrl = field.controlType === 'collapse';
  const currentColKeys: string[] = isCollapseCtrl && field.settings.columns
    ? String(field.settings.columns).split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];
  const toggleColumn = (childKey: string) => {
    if (!childKey) return;
    const isOn = currentColKeys.includes(childKey);
    const next = isOn ? currentColKeys.filter(k => k !== childKey) : [...currentColKeys, childKey];
    update({ settings: { ...field.settings, columns: next.length > 0 ? next.join(', ') : undefined } });
  };

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: '8px', bgcolor: bg, mb: 1, overflow: 'visible', transition: 'border-color 0.15s' }}>
      {/* Header */}
      <Box onClick={() => setExpanded(!expanded)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.2, cursor: 'pointer',
          bgcolor: expanded ? `${meta?.color || '#005e5d'}08` : bg, transition: 'background 0.15s', userSelect: 'none',
          borderRadius: expanded ? '8px 8px 0 0' : '8px' }}>
        <DragHandleIcon sx={{ color: '#ccc', fontSize: 18, flexShrink: 0 }} />
        <Box sx={{ color: meta?.color || '#999', display: 'flex', alignItems: 'center', flexShrink: 0 }}>{meta?.icon}</Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }} onClick={e => e.stopPropagation()}>
          <TextField variant="standard" size="small" placeholder="Field label..." value={localLabel}
            onChange={e => { setLocalLabel(e.target.value); debouncedTextChange({ label: e.target.value }); }}
            onBlur={flushPending}
            sx={{ '& .MuiInput-root': { fontSize: '13px', fontWeight: 600 }, '& .MuiInput-underline:before': { borderBottomColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderBottomColor: '#ddd !important' } }} />
          <TextField variant="standard" size="small" placeholder="prop_name (key)" value={localPropName}
            onChange={e => { setLocalPropName(e.target.value); debouncedTextChange({ propName: e.target.value }); }}
            onBlur={flushPending}
            sx={{ '& .MuiInput-root': { fontSize: '10px', color: '#aaa', fontFamily: 'monospace' }, '& .MuiInput-underline:before': { borderBottomColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderBottomColor: '#eee !important' } }} />
        </Box>
        <Chip label={meta?.label || field.controlType} size="small"
          sx={{ bgcolor: `${meta?.color || '#999'}15`, color: meta?.color || '#666', fontSize: '10px', height: 20, fontWeight: 600, flexShrink: 0 }} />
        {hasChilds && field.childs.length > 0 && (
          <Chip label={`${field.childs.length}c`} size="small" sx={{ bgcolor: '#f0f0f0', color: '#888', fontSize: '10px', height: 18, flexShrink: 0 }} />
        )}
        {isTab && (field.tabChilds || []).length > 0 && (
          <Chip label={`${field.tabChilds.length}t`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#0288D1', fontSize: '10px', height: 18, flexShrink: 0 }} />
        )}
        {!hideDelete && (
          <Tooltip title="Delete field">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(); }}
              sx={{ color: '#ffaaaa', '&:hover': { color: '#f44336' }, flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <ExpandMoreIcon sx={{ color: '#bbb', fontSize: 18, flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </Box>

      {/* Expanded settings */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 1.5, borderTop: `1px solid ${border}40` }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Type + Required */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1 }}>
                <BaseSelect
                  label="Control Type"
                  size="small"
                  fullWidth
                  value={field.controlType}
                  onChange={e => update({ controlType: e.target.value as string, settings: {}, childs: [], tabChilds: [] })}
                  options={Object.entries(CONTROL_REGISTRY).map(([type, entry]) => ({ label: entry.label, value: type }))}
                />
              </Box>
              <BaseCheckbox
                label="Required"
                checked={field.required}
                onChange={checked => update({ required: checked })}
              />
            </Box>

            {/* Description */}
            <BaseTextField
              label="Description"
              size="small"
              fullWidth
              placeholder="Help text shown below the field"
              value={localDesc}
              onChange={e => { setLocalDesc(e.target.value); debouncedTextChange({ description: e.target.value }); }}
              onBlur={flushPending}
              helperText="optional"
            />
            {/* Control settings */}
            {(CONTROL_REGISTRY[field.controlType]?.settingsConfig || []).length > 0 && (
              <Box>
                <Divider sx={{ mb: 1.5 }}><Typography variant="caption" sx={{ color: '#888', fontWeight: 700, px: 1 }}>Settings</Typography></Divider>
                <FieldSettingsForm controlType={field.controlType} settings={field.settings} onChange={s => update({ settings: s })} />
              </Box>
            )}
            {/* Tab control: named-tab children */}
            {isTab && (
              <TabControlEditor
                tabChilds={field.tabChilds || []}
                onChange={tc => update({ tabChilds: tc })}
                depth={depth}
              />
            )}
            {/* Block/array/collapse: array children */}
            {!isTab && hasChilds && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f8f9', borderRadius: '6px' }}>
                <Divider sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: meta?.color || '#005e5d', fontWeight: 700, px: 1 }}>Child Fields ({field.childs.length})</Typography>
                </Divider>
                <Box sx={{ pl: 1.5, borderLeft: `2px solid ${(meta?.color || '#005e5d')}40`, display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {field.childs.map((child, i) => {
                    if (!isCollapseCtrl) {
                      return <FieldCard key={child.id} field={child} onChange={f => updateChild(i, f)} onDelete={() => removeChild(i)} depth={depth + 1} />;
                    }
                    const childKey = child.propName || child.label?.toLowerCase().replace(/\s+/g, '_') || '';
                    const isChecked = !!childKey && currentColKeys.includes(childKey);
                    return (
                      <Box key={child.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <FieldCard field={child} onChange={f => updateChild(i, f)} onDelete={() => removeChild(i)} depth={depth + 1} />
                        </Box>
                        <Tooltip title={isChecked ? 'Hide from table columns' : 'Show as table column'}>
                          <Box
                            sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', userSelect: 'none', px: 0.25 }}
                          >
                            <Switch
                              size="small"
                              checked={isChecked}
                              onChange={() => toggleColumn(childKey)}
                              onClick={e => e.stopPropagation()}
                              sx={{ '& .MuiSwitch-track': { bgcolor: isChecked ? '#005e5d !important' : undefined } }}
                            />
                            <Typography variant="caption" sx={{ color: isChecked ? '#005e5d' : '#bbb', fontSize: '9px', fontWeight: 700, lineHeight: 1 }}>COL</Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    );
                  })}
                  <Box sx={{ mt: 1.5 }}>
                    <AddFieldButton onAdd={addChild} label="Add Child Field" />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

// --- FieldList ---

const FieldList: React.FC<{
  fields: FieldDef[];
  onChange: (fields: FieldDef[]) => void;
  emptyLabel?: string;
}> = ({ fields, onChange, emptyLabel }) => {
  const add    = (type: string) => onChange([...fields, newField(type)]);
  const update = (i: number, f: FieldDef) => { const n = [...fields]; n[i] = f; onChange(n); };
  const remove = (i: number) => onChange(fields.filter((_, x) => x !== i));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 1, pr: 0.5 }}>
        {fields.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center', border: '2px dashed #eee', borderRadius: '10px', mb: 1 }}>
            <ViewStream sx={{ fontSize: 44, mb: 1, color: '#ddd' }} />
            <Typography variant="body2" sx={{ color: '#bbb' }}>{emptyLabel ?? 'No fields yet'}</Typography>
            <Typography variant="caption" sx={{ color: '#ccc' }}>Click "Add Field" below to start</Typography>
          </Box>
        ) : (
          fields.map((f, i) => <FieldCard key={f.id} field={f} onChange={u => update(i, u)} onDelete={() => remove(i)} />)
        )}
      </Box>
      <AddFieldButton onAdd={add} />
      {fields.length > 0 && (
        <Typography variant="caption" sx={{ color: '#bbb', textAlign: 'center', mt: 0.75 }}>
          {fields.length} field{fields.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  );
};

// --- Main SchemaBuilder ---

export interface SchemaBuilderProps {
  value: string;
  onChange: (json: string) => void;
}

export const SchemaBuilder: React.FC<SchemaBuilderProps> = ({ value, onChange }) => {
  const [fields, setFields] = useState<FieldDef[]>([]);
  const prevValueRef = useRef(value);

  useEffect(() => {
    setFields(parseSchemaToFields(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setFields(parseSchemaToFields(value));
    }
  }, [value]);

  const handleChange = (next: FieldDef[]) => {
    setFields(next);
    const json = JSON.stringify(buildSchemaFromFields(next), null, 2);
    prevValueRef.current = json;
    onChange(json);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FieldList fields={fields} onChange={handleChange} />
    </Box>
  );
};

export default SchemaBuilder;
